const MAX_DIMENSION = 2000;

/**
 * Resize an image file to fit within MAX_DIMENSION x MAX_DIMENSION,
 * then upload to S3 via the storage proxy.
 * Returns the public URL.
 */
export async function resizeAndUploadImage(file: File): Promise<string> {
  const resized = await resizeImage(file, MAX_DIMENSION);
  return uploadBlob(resized, file.name);
}

function resizeImage(file: File, maxSize: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;

      if (width <= maxSize && height <= maxSize) {
        // No resize needed, return original
        resolve(file);
        return;
      }

      const scale = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * scale);
      height = Math.round(height * scale);

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error("Failed to resize image"));
        },
        file.type.startsWith("image/png") ? "image/png" : "image/jpeg",
        0.9,
      );
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = URL.createObjectURL(file);
  });
}

async function uploadBlob(blob: Blob, fileName: string): Promise<string> {
  const res = await fetch("/api/storage/upload", {
    method: "POST",
    headers: {
      "Content-Type": blob.type || "image/jpeg",
      "X-File-Name": fileName,
    },
    body: blob,
  });

  if (!res.ok) throw new Error("Failed to upload image");
  const { publicUrl } = await res.json();
  return publicUrl;
}
